import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export interface StatusDef {
  key: string;
  label: string;
  canonical: "open" | "in_progress" | "waiting" | "done" | "cancelled";
  color?: string;
}
export interface Space {
  id: string;
  key: string;
  name: string;
  description: string;
  companyId: string;
  businessUnitId: string | null;
  teamId: string | null;
  statuses: StatusDef[];
  verification: boolean;
  defaultSensitivity: string;
}
export interface Item {
  id: string;
  ref: string;
  kind: string;
  title: string;
  description: string;
  priority: "P1" | "P2" | "P3" | "P4";
  spaceId: string;
  statusKey: string;
  state: StatusDef["canonical"];
  pendingVerification: boolean;
  ownerPersonId: string | null;
  assigneeIds: string[];
  poolTeamId: string | null;
  dueAt: string | null;
  plannedDate: string | null;
  focusWeek: string | null;
  mustDo: boolean;
  personal?: boolean;
  nextAction?: string | null;
  followUpOn?: string | null;
  horizon?: "next" | "later" | "someday" | null;
  repeat?: import("./repeat").RepeatRule | null;
  seriesId?: string | null;
  createdBy?: string | null;
  notesHtml?: string;
  subtasks?: { total: number; done: number } | null;
  parentId?: string | null;
  source?: string;
  category?: { id: string; name: string; color: string } | null;
  decisionNeeded?: boolean;
  slaDueAt: string | null;
  slaState: string;
  waitingOn: { personId?: string; external?: string; reason?: string } | null;
  blockedReason: string | null;
  evidencePolicy: "none" | "optional" | "photo_required";
  sensitivity: string;
  requestId: string | null;
  version: number;
  createdAt: string;
  completedAt: string | null;
  spaceName?: string;
  statusLabel?: string;
  assigneeNames?: string[];
}
export interface Person {
  id: string;
  name: string;
  email: string | null;
  /** added ahead of time, has not signed in yet */
  pending?: boolean;
}
export interface FieldDef {
  key: string;
  label: string;
  type: "text" | "textarea" | "number" | "date" | "datetime" | "select" | "checkbox" | "person" | "site" | "company" | "department" | "team" | "email" | "phone";
  required?: boolean;
  options?: string[];
  help?: string;
}
export interface RequestType {
  id: string;
  key: string;
  version: number;
  name: string;
  description: string;
  icon: string;
  category: string;
  spaceId: string;
  spaceName: string;
  form: FieldDef[];
  slaHours: number | null;
  defaultPriority: string;
  execution: string;
}

export const useSpaces = () =>
  useQuery({ queryKey: ["spaces"], queryFn: () => api<{ items: Space[] }>("/api/work/spaces"), staleTime: 5 * 60_000 });
export const useRequestTypes = () =>
  useQuery({ queryKey: ["request-types"], queryFn: () => api<{ items: RequestType[] }>("/api/requests/types"), staleTime: 5 * 60_000 });

export const PRIORITY_LABEL: Record<string, string> = { P1: "Urgent", P2: "High", P3: "Normal", P4: "Low" };

export function dueLabel(due: string | null): { text: string; tone: "late" | "soon" | "ok" | "none" } {
  if (!due) return { text: "No due date", tone: "none" };
  const d = new Date(due);
  const now = new Date();
  const ms = d.getTime() - now.getTime();
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now.getTime() + 86400000).toDateString() === d.toDateString();
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (ms < 0) {
    const days = Math.floor(-ms / 86400000);
    return { text: days >= 1 ? `${days}d overdue` : `Overdue (${time})`, tone: "late" };
  }
  if (sameDay) return { text: `Today ${time}`, tone: "soon" };
  if (tomorrow) return { text: `Tomorrow ${time}`, tone: "soon" };
  return { text: d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }), tone: "ok" };
}

/** The star: a must-do stays starred, and first in lists, until the task is done. Separate from the Sort week. */
export function isMustDo(i: { mustDo?: boolean; state: string }): boolean {
  return i.state === "open" && !!i.mustDo;
}

/** Monday of the current week (local) as YYYY-MM-DD — the ★ must-do week key. */
export function thisMonday(): string {
  const d = new Date();
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day + (day >= 5 ? 7 : 0)); // Saturday and Sunday: the coming week
  return d.toLocaleDateString("en-CA");
}
